/**
 * `notifications` endpoint wrappers — `auth/notifications/`, mounted under
 * the same `/api/auth` prefix as the rest of the users module (see
 * refactor/backend/src/modules/users/routes.ts). Every row is scoped to the
 * caller's own notifications, for every role.
 *
 * No `createNotification` wrapper: notifications are system-generated in
 * practice, so no page here builds a create form, and this file only covers
 * the read / mark-as-read / delete surface those pages actually use.
 */
import { apiClient } from '../client';
import { AUTH_NOTIFICATIONS } from './paths';
import type { Paginated } from '../types';
import type { Notification } from '../../types/notification';

/** `GET auth/notifications/` — paginated, scoped to the caller. */
export async function listNotifications(params?: {
  page?: number | undefined;
}): Promise<Paginated<Notification>> {
  const response = await apiClient.get<Paginated<Notification>>(AUTH_NOTIFICATIONS, { params });
  return response.data;
}

/**
 * `POST auth/notifications/{id}/mark_as_read/` — DRF `detail=True` action.
 * A notification belonging to another user 404s rather than 403s (the
 * backend scopes the lookup itself, not just the mutation).
 */
export async function markAsRead(id: number): Promise<Notification> {
  const response = await apiClient.post<Notification>(`${AUTH_NOTIFICATIONS}${id}/mark_as_read/`);
  return response.data;
}

/** `POST auth/notifications/mark_all_as_read/` — DRF `detail=False` action. */
export async function markAllAsRead(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    `${AUTH_NOTIFICATIONS}mark_all_as_read/`,
  );
  return response.data;
}

/** `DELETE auth/notifications/{id}/`. */
export async function deleteNotification(id: number): Promise<void> {
  await apiClient.delete(`${AUTH_NOTIFICATIONS}${id}/`);
}
